import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './TextToSpeechDialog.less';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { CloseOutlined } from '@ant-design/icons';
import { ClipLoader } from 'react-spinners';
import {
  getVoices,
  extractLanguages,
  generateAudio,
  Voice,
} from '../../services/textToSpeechService';
import {
  InsufficientPixlesError,
  NoActiveEntitlementError,
} from '../../services/entitlementErrors';

interface TextToSpeechDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAudioGenerated: (
    audioBlob: Blob,
    duration: string,
    fileName: string
  ) => void;
}

const TextToSpeechDialog: React.FC<TextToSpeechDialogProps> = ({
  isOpen,
  onClose,
  onAudioGenerated,
}) => {
  const [step, setStep] = useState(1);
  const [inputText, setInputText] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [lang, setLang] = useState('');
  const [displayLang, setDisplayLang] = useState('');
  const [voiceURI, setVoiceURI] = useState('');
  const [selectedCharacter, setSelectedCharacter] = useState('');
  const [selectedTone, setSelectedTone] = useState('');
  const [availableTones, setAvailableTones] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAudioBlob, setGeneratedAudioBlob] = useState<Blob | null>(
    null
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [fileName, setFileName] = useState('');
  const [duration, setDuration] = useState('00:00:00');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Credit-gate upsell for voiceover generation (402 → top-up, 403 → subscribe).
  const [billingGate, setBillingGate] = useState<
    'insufficient' | 'entitlement' | null
  >(null);
  const navigate = useNavigate();
  const [langToLocaleMap, setLangToLocaleMap] = useState<
    Record<string, string>
  >({});

  // Function to fetch voices from the API
  const fetchVoices = async () => {
    setError(null);
    try {
      const response = await getVoices();
      setVoices(response.voices);
      setLanguages(extractLanguages(response.voices));

      // Create a mapping of display language to locale
      const langMap: Record<string, string> = {};
      response.voices.forEach((voice) => {
        // Use the first locale for each display language
        if (
          voice.displayLanguage &&
          Object.keys(voice.supportedLocales).length > 0
        ) {
          langMap[voice.displayLanguage] = Object.keys(
            voice.supportedLocales
          )[0];
        }
      });
      setLangToLocaleMap(langMap);
    } catch (error) {
      console.error('Error fetching voices:', error);
      setError('Failed to load voices. Please try again later.');
    }
  };

  // Update available tones when language or character changes
  useEffect(() => {
    if (lang && selectedCharacter) {
      const selectedVoice = voices.find(
        (voice) => voice.voiceId === selectedCharacter
      );
      if (selectedVoice && selectedVoice.supportedLocales[lang]) {
        setAvailableTones(selectedVoice.supportedLocales[lang].availableStyles);
      } else {
        setAvailableTones([]);
      }
    } else {
      setAvailableTones([]);
    }
  }, [lang, selectedCharacter, voices]);

  // Update voiceURI when character and tone are selected
  useEffect(() => {
    if (selectedCharacter && selectedTone) {
      setVoiceURI(selectedCharacter);
    } else {
      setVoiceURI('');
    }
  }, [selectedCharacter, selectedTone]);

  // Fetch voices when component mounts
  useEffect(() => {
    if (isOpen) {
      fetchVoices();
    }
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(ms).padStart(3, '0')}`;
  };

  const handleSave = () => {
    if (!generatedAudioBlob || !fileName) return;

    onAudioGenerated(generatedAudioBlob, duration, `${fileName}.mp3`);
    resetState();
    onClose();
  };

  const resetState = () => {
    setStep(1);
    setInputText('');
    setLang('');
    setDisplayLang('');
    setVoiceURI('');
    setSelectedCharacter('');
    setSelectedTone('');
    setAvailableTones([]);
    setIsGenerating(false);
    setGeneratedAudioBlob(null);
    setIsPlaying(false);
    setFileName('');
    setDuration('00:00:00');
    setError(null);
    setBillingGate(null);

    // Clean up audio resources
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Revoke any object URLs to prevent memory leaks
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  };

  const setupAudio = (blob: Blob) => {
    // Clean up previous audio if it exists
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeEventListener(
        'loadedmetadata',
        handleMetadataLoaded
      );
      audioRef.current.removeEventListener('ended', handleAudioEnded);
    }

    // Revoke previous URL if it exists
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
    }

    // Create new audio element and URL
    audioUrlRef.current = URL.createObjectURL(blob);
    audioRef.current = new Audio(audioUrlRef.current);

    // Set up event listeners
    audioRef.current.addEventListener('loadedmetadata', handleMetadataLoaded);
    audioRef.current.addEventListener('ended', handleAudioEnded);
  };

  const handleMetadataLoaded = () => {
    if (audioRef.current) {
      setDuration(formatTime(audioRef.current?.duration || 0));
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handlePlayPause = () => {
    if (!generatedAudioBlob) return;

    // If audio ref doesn't exist yet, set it up
    if (!audioRef.current) {
      setupAudio(generatedAudioBlob);
    }

    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch((error) => {
        console.error('Error playing audio:', error);
      });
      setIsPlaying(true);
    }
  };

  const generateAudioFromText = async () => {
    if (!inputText || !lang || !voiceURI || !selectedTone) return;

    setIsGenerating(true);
    setError(null);
    // Fresh attempt: clear any prior credit-gate upsell.
    setBillingGate(null);
    try {
      const audioResponse = await generateAudio(
        inputText,
        voiceURI,
        selectedTone
      );
      setupAudio(audioResponse.blob);
      setGeneratedAudioBlob(audioResponse.blob);
      setDuration(formatTime(audioResponse.audioLengthInSeconds));
      setIsGenerating(false);
      setStep(3);
    } catch (error: any) {
      console.error('Error generating audio:', error);
      if (error instanceof NoActiveEntitlementError) {
        setBillingGate('entitlement');
        setError(
          error.message || 'An active subscription is required for voiceover.'
        );
      } else if (error instanceof InsufficientPixlesError) {
        setBillingGate('insufficient');
        setError(error.message || 'Insufficient credits for this voiceover.');
      } else {
        setError('Failed to generate audio. Please try again later.');
      }
      setIsGenerating(false);
    }
  };

  const handleNext = () => {
    if (step === 1 && inputText.trim()) {
      setStep(2);
    } else if (step === 2 && lang && voiceURI) {
      generateAudioFromText();
    } else if (step === 3 && fileName.trim() && generatedAudioBlob) {
      handleSave();
    }
  };

  // Setup effect to initialize audio when generatedAudioBlob changes
  useEffect(() => {
    if (generatedAudioBlob) {
      setupAudio(generatedAudioBlob);
    }
  }, [generatedAudioBlob]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      // Cleanup function for component unmounting
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener(
          'loadedmetadata',
          handleMetadataLoaded
        );
        audioRef.current.removeEventListener('ended', handleAudioEnded);
        audioRef.current = null;
      }

      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="text-to-speech-dialog" onClick={handleOverlayClick}>
      <div
        className={`dialog-content ${step === 2 && !isGenerating ? 'dialog-content-2' : isGenerating ? 'dialog-content-2-generating' : ''}`}
      >
        {step === 1 && (
          <>
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Enter text to convert to speech..."
              maxLength={2500}
            />
          </>
        )}

        {step === 2 && !isGenerating && (
          <>
            <div className="voice-selection">
              <div className="select-group">
                <label htmlFor="lang">Language</label>
                <select
                  id="lang"
                  value={displayLang}
                  onChange={(e) => {
                    const selectedDisplayLang = e.target.value;
                    setDisplayLang(selectedDisplayLang);
                    setLang(langToLocaleMap[selectedDisplayLang] || '');
                    setSelectedCharacter('');
                    setSelectedTone('');
                    setVoiceURI('');
                  }}
                >
                  <option value="">Choose a language</option>
                  {languages.map((displayLang: string) => (
                    <option key={displayLang} value={displayLang}>
                      {displayLang}
                    </option>
                  ))}
                </select>
              </div>

              <div className="select-group">
                <label htmlFor="character">Character</label>
                <select
                  id="character"
                  value={selectedCharacter}
                  onChange={(e) => {
                    setSelectedCharacter(e.target.value);
                    setSelectedTone('');
                    setVoiceURI('');
                  }}
                  disabled={!lang}
                >
                  <option value="">Choose a character</option>
                  {voices
                    .filter((voice: Voice) => {
                      // Check if the voice supports the selected language
                      return (
                        !lang ||
                        Object.keys(voice.supportedLocales).includes(lang)
                      );
                    })
                    .map((voice: Voice) => (
                      <option key={voice.voiceId} value={voice.voiceId}>
                        {voice.displayName} ({voice.gender}) -{' '}
                        {voice.description}
                      </option>
                    ))}
                </select>
              </div>

              <div className="select-group">
                <label htmlFor="tone">Tone</label>
                <select
                  id="tone"
                  value={selectedTone}
                  onChange={(e) => setSelectedTone(e.target.value)}
                  disabled={!selectedCharacter}
                >
                  <option value="">Choose a tone</option>
                  {availableTones.map((tone: string) => (
                    <option key={tone} value={tone}>
                      {tone}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {isGenerating && (
          <div className="generating-step">
            <div className="loading-circle">
              <ClipLoader size={50} color="#36CEDC" />
            </div>
            <p>Generating audio...</p>
          </div>
        )}

        {step === 3 && !isGenerating && (
          <div className="preview-step">
            <div className="audio-preview">
              <button
                className="play-button"
                onClick={handlePlayPause}
                disabled={!generatedAudioBlob}
              >
                {isPlaying ? (
                  <PauseIcon style={{ fontSize: '32px' }} />
                ) : (
                  <PlayArrowIcon style={{ fontSize: '32px' }} />
                )}
              </button>
              <div className="duration">{duration}</div>
            </div>
            <div className="file-name-input-tts">
              <label>File Name</label>
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Enter file name here"
              />
              <button
                className="save-button"
                onClick={handleSave}
                disabled={!fileName}
              >
                Save to timeline
              </button>
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
        {billingGate && (
          <button
            type="button"
            className="billing-gate-button"
            onClick={() => navigate('/billing')}
          >
            {billingGate === 'entitlement'
              ? 'Subscribe to Pro'
              : 'Top up credits'}
          </button>
        )}

        <div className="dialog-actions">
          {step === 1 && (
            <div className="character-count">
              {inputText.length}/2500 characters
            </div>
          )}
          {(step === 2 || step === 3) && !isGenerating && (
            <button
              className="back-button"
              onClick={() => setStep(step - 1)}
              disabled={isGenerating}
            >
              <ChevronLeftIcon
                style={{
                  fontSize: '24px',
                  color: 'white',
                }}
              />
              <span>Back</span>
            </button>
          )}
          {step === 1 || (step === 2 && !isGenerating) ? (
            <button
              className="next-button"
              onClick={handleNext}
              disabled={
                isGenerating ||
                (step === 1 && !inputText.trim()) ||
                (step === 2 && (!lang || !voiceURI))
              }
            >
              <span>Next</span>
              <ChevronRightIcon
                style={{
                  fontSize: '24px',
                  color:
                    isGenerating ||
                    (step === 1 && !inputText.trim()) ||
                    (step === 2 && (!lang || !voiceURI))
                      ? 'white'
                      : '#8FE968',
                }}
              />
            </button>
          ) : (
            !isGenerating && (
              <div className="control-buttons">
                <button
                  className="control-button reject"
                  onClick={onClose}
                  title="Discard"
                >
                  <CloseOutlined />
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default TextToSpeechDialog;
